import { type Account, accountKeys, type AccountClass } from '@taler/api-client'
import { useQueries } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { keepPreviousListResult, sameLedgerAndClass } from '@/app/keep-previous-list'

export interface UseRollupAccountsOptions {
  readonly ledgerId: () => string | undefined
  readonly accountClass: () => AccountClass
  readonly enabled: () => boolean
}

export interface UseRollupAccounts {
  readonly accounts: ComputedRef<readonly Account[] | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  retry(): void
}

/** `archived` and `hidden` are independent gates; only all four combinations cover every account. */
const VISIBILITY_COMBINATIONS = [
  {},
  { archived: true },
  { hidden: true },
  { archived: true, hidden: true },
] as const

/** Every account a roll-up of one class can name, across all four visibility combinations. */
export function useRollupAccounts(options: UseRollupAccountsOptions): UseRollupAccounts {
  const api = useApi()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const listQueries = useQueries({
    queries: computed(() =>
      VISIBILITY_COMBINATIONS.map((visibility) => {
        const listQuery = { class: options.accountClass(), limit: 100, ...visibility }
        return {
          queryKey: [...accountKeys.list(options.ledgerId() ?? '', listQuery), listQuery.class],
          placeholderData: keepPreviousListResult(sameLedgerAndClass(options.ledgerId(), listQuery.class)),
          queryFn: async ({ signal }: { signal?: AbortSignal }) => {
            const items: Account[] = []
            let cursor: string | undefined
            do {
              const page = await api.accounts.list(
                options.ledgerId()!,
                cursor === undefined ? listQuery : { ...listQuery, cursor },
                signal,
              )
              items.push(...page.items)
              cursor = page.nextCursor
            } while (cursor !== undefined)
            return items
          },
          enabled: active.value,
          retry: false,
        }
      }),
    ),
  })

  const accounts = computed(() => {
    if (listQueries.value.some((query) => query.data === undefined)) return undefined
    return listQueries.value.flatMap((query) => query.data ?? [])
  })

  return {
    accounts,
    isLoading: computed(() => active.value && listQueries.value.some((query) => query.isPending)),
    error: computed(() => listQueries.value.find((query) => query.error)?.error ?? null),
    retrying: computed(() => listQueries.value.some((query) => query.isError && query.isFetching)),
    retry: () => {
      for (const query of listQueries.value) void query.refetch()
    },
  }
}
